import React, { useState } from 'react';
import { Mail, Phone, MapPin, ArrowUpRight } from 'lucide-react';
import PageHero from '../components/PageHero';
import useReveal from '../hooks/useReveal';
import { useToast } from '../hooks/use-toast';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { contact, people, images } from '../mock';
import { trackEvent } from '../lib/consent';

const initial = { firstName: '', lastName: '', email: '', company: '', message: '', website: '' };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabTrigger =
  'rounded-none bg-transparent px-0 pb-4 text-[11px] uppercase tracking-[0.2em] text-stone font-normal border-b-2 border-transparent -mb-px data-[state=active]:bg-transparent data-[state=active]:text-ink data-[state=active]:border-ink data-[state=active]:shadow-none transition-colors';

const Contact = () => {
  const ref = useReveal();
  const { toast } = useToast();
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(res.status === 429 ? 'rate' : 'send');
      trackEvent('generate_lead', { form_name: 'kontaktformular' });
      toast({
        title: 'Tak for din henvendelse',
        description: 'Din besked er sendt. Vi vender tilbage hurtigst muligt.',
      });
      setForm(initial);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Beskeden blev ikke sendt',
        description:
          err.message === 'rate'
            ? 'Du har sendt mange henvendelser på kort tid. Prøv igen om lidt.'
            : `Prøv igen, eller skriv direkte til ${contact.email}.`,
      });
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    'w-full bg-transparent border-b border-ink/25 py-3 text-sm font-light text-ink placeholder:text-stone/70 focus:outline-none focus:border-ink transition-colors';

  return (
    <div ref={ref}>
      <PageHero eyebrow="Kontakt" title="Lad os tale om projektet" image={images.varsoLounge} />

      <section className="bg-bone">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 py-20 md:py-28">
          <Tabs defaultValue="kontakt" className="w-full">
            <TabsList className="h-auto w-full justify-start gap-8 md:gap-12 rounded-none bg-transparent p-0 border-b border-ink/15 flex-wrap">
              <TabsTrigger value="kontakt" className={tabTrigger}>
                Kontakt
              </TabsTrigger>
              <TabsTrigger value="personer" className={tabTrigger}>
                Kontaktpersoner
              </TabsTrigger>
              <TabsTrigger value="ansogninger" className={tabTrigger}>
                Uopfordrede ansøgninger
              </TabsTrigger>
              <TabsTrigger value="bogholderi" className={tabTrigger}>
                Bogholderi
              </TabsTrigger>
            </TabsList>

            {/* TAB — KONTAKT (form + info) */}
            <TabsContent value="kontakt" className="mt-14 md:mt-20">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
                <div className="md:col-span-5">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-stone">
                    {contact.company}
                  </p>
                  <h2 className="mt-5 font-serif font-light text-3xl md:text-4xl leading-tight">
                    Klar til en løsningsorienteret dialog
                  </h2>

                  <div className="mt-12 space-y-8">
                    <div className="flex items-start gap-5">
                      <MapPin size={18} strokeWidth={1.3} className="mt-1 shrink-0 text-clay" />
                      <div className="text-sm font-light text-ink/80">
                        <div>{contact.address}</div>
                        <div>{contact.zip}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-5">
                      <Mail size={18} strokeWidth={1.3} className="mt-1 shrink-0 text-clay" />
                      <a
                        href={`mailto:${contact.email}`}
                        className="link-underline text-sm font-light text-ink/80"
                      >
                        {contact.email}
                      </a>
                    </div>
                    <div className="flex items-start gap-5">
                      <Phone size={18} strokeWidth={1.3} className="mt-1 shrink-0 text-clay" />
                      <a
                        href={`tel:${contact.phoneRaw}`}
                        className="link-underline text-sm font-light text-ink/80"
                      >
                        Tlf. {contact.phone}
                      </a>
                    </div>
                  </div>

                  <div className="mt-12 pt-8 border-t border-ink/10 text-[11px] uppercase tracking-[0.2em] text-stone">
                    CVR: {contact.cvr}
                  </div>
                </div>

                <div className="md:col-span-6 md:col-start-7">
                  <form onSubmit={handleSubmit} className="space-y-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                      <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] text-stone">
                          Fornavn
                        </label>
                        <input
                          name="firstName"
                        data-testid="contact-input-firstName"
                          value={form.firstName}
                          onChange={handleChange}
                          required
                          className={inputClass}
                          placeholder="Fornavn"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] text-stone">
                          Efternavn
                        </label>
                        <input
                          name="lastName"
                        data-testid="contact-input-lastName"
                          value={form.lastName}
                          onChange={handleChange}
                          required
                          className={inputClass}
                          placeholder="Efternavn"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.2em] text-stone">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        data-testid="contact-input-email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        className={inputClass}
                        placeholder="navn@virksomhed.dk"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.2em] text-stone">
                        Virksomhed
                      </label>
                      <input
                        name="company"
                        data-testid="contact-input-company"
                        value={form.company}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Virksomhed"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.2em] text-stone">
                        Besked
                      </label>
                      <textarea
                        name="message"
                        data-testid="contact-input-message"
                        value={form.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className={`${inputClass} resize-none`}
                        placeholder="Fortæl om projektet…"
                      />
                    </div>
                    <input
                      type="text"
                      name="website"
                      value={form.website}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      className="hidden"
                    />
                    <button
                      type="submit"
                      data-testid="contact-submit-btn"
                      disabled={sending}
                      className="inline-flex items-center gap-3 bg-ink text-bone px-10 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-ink/85 transition-colors disabled:opacity-60 disabled:cursor-wait"
                    >
                      {sending ? 'Sender…' : 'Send henvendelse'}
                    </button>
                  </form>
                </div>
              </div>
            </TabsContent>

            {/* TAB — KONTAKTPERSONER */}
            <TabsContent value="personer" className="mt-14 md:mt-20">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 md:gap-16 max-w-4xl">
                {people.map((p) => (
                  <div key={p.email} data-testid="contact-person" className="border-t border-ink/15 pt-8">
                    <h3 className="font-serif text-3xl font-light">{p.name}</h3>
                    <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-stone">{p.title}</p>
                    <div className="mt-6 flex flex-col items-start gap-3">
                      <a
                        href={`tel:${p.phoneRaw}`}
                        data-testid="contact-person-phone"
                        className="link-underline text-base font-light text-ink/80"
                      >
                        Tlf. {p.phone}
                      </a>
                      <a
                        href={`mailto:${p.email}`}
                        data-testid="contact-person-email"
                        className="inline-flex items-center gap-3 text-base font-light text-ink border-b border-ink/40 pb-1 hover:border-ink transition-colors"
                      >
                        {p.email}
                        <ArrowUpRight size={16} strokeWidth={1.4} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* TAB — UOPFORDREDE ANSØGNINGER */}
            <TabsContent value="ansogninger" className="mt-14 md:mt-20">
              <div className="max-w-2xl">
                <p className="text-[11px] uppercase tracking-[0.3em] text-stone">Karriere</p>
                <h2 className="mt-5 font-serif font-light text-3xl md:text-4xl leading-tight">
                  Uopfordrede ansøgninger
                </h2>
                <p className="mt-6 text-base font-light leading-relaxed text-ink/80">
                  Vi er altid interesserede i at møde dygtige fagfolk, der deler vores tilgang til
                  faglighed, dokumentation og samarbejde. Har du lyst til at være en del af Kepp &
                  Co., modtager vi gerne din uopfordrede ansøgning.
                </p>
                <p className="mt-6 text-base font-light leading-relaxed text-ink/80">
                  Send din ansøgning og dit CV til:
                </p>
                <a
                  href={`mailto:${contact.jobEmail}`}
                  className="mt-6 inline-flex items-center gap-3 text-lg font-light text-ink border-b border-ink/40 pb-1 hover:border-ink transition-colors"
                >
                  {contact.jobEmail}
                  <ArrowUpRight size={18} strokeWidth={1.4} />
                </a>
              </div>
            </TabsContent>

            {/* TAB — BOGHOLDERI */}
            <TabsContent value="bogholderi" className="mt-14 md:mt-20">
              <div className="max-w-2xl">
                <p className="text-[11px] uppercase tracking-[0.3em] text-stone">Administration</p>
                <h2 className="mt-5 font-serif font-light text-3xl md:text-4xl leading-tight">
                  Bogholderi & faktura
                </h2>
                <p className="mt-6 text-base font-light leading-relaxed text-ink/80">
                  Fakturaer sendes elektronisk til vores EAN-nummer.
                </p>
                <dl className="mt-8 space-y-6">
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.2em] text-stone">EAN-nummer</dt>
                    <dd data-testid="billing-ean" className="mt-2 text-lg font-light text-ink">
                      {contact.ean}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.2em] text-stone">
                      Betalingsbetingelser
                    </dt>
                    <dd data-testid="billing-terms" className="mt-2 text-lg font-light text-ink">
                      {contact.paymentTerms}
                    </dd>
                  </div>
                </dl>
                <div className="mt-10 pt-8 border-t border-ink/10 text-sm font-light text-stone">
                  {contact.company} · {contact.address}, {contact.zip} · CVR: {contact.cvr}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default Contact;
